// A hole's stroke index decides which holes a player's shots fall on, so a
// card's 18 SIs are not 18 independent numbers — they are a PERMUTATION of
// 1–18. A duplicate silently gives a shot twice and withholds it somewhere
// else, which is why every SI editor validates the whole card rather than the
// cell being typed into.
//
// Extracted from the course wizard's Pars step so the in-round course-data
// correction (CourseStatsEditModal) applies exactly the same rule.

// Empty means "not set" — '' , null and undefined are all blanks; anything
// outside 1–18 is not an SI at all.
const asSI = (v) => {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= 18 ? n : null;
};

export const isBlank = (v) => v === '' || v === null || v === undefined;

/**
 * @returns {{duplicates: Set<number>, dupHoles: Set<number>, missing: number[], complete: boolean, empty: boolean}}
 *   `complete` is the only question most callers ask: is this a valid 1–18 card.
 */
export const analyseSI = (sis = []) => {
  const counts = {};
  sis.forEach((v) => {
    const n = asSI(v);
    if (n) counts[n] = (counts[n] || 0) + 1;
  });
  const duplicates = new Set(Object.keys(counts).filter(k => counts[k] > 1).map(Number));
  const dupHoles = new Set();
  sis.forEach((v, idx) => { if (duplicates.has(asSI(v))) dupHoles.add(idx); });
  const missing = [];
  for (let n = 1; n <= 18; n++) if (!counts[n]) missing.push(n);

  const empty = sis.length === 0 || sis.every(isBlank);
  return {
    duplicates,
    dupHoles,
    missing,
    empty,
    complete: sis.length === 18 && missing.length === 0 && duplicates.size === 0,
  };
};

/**
 * One-tap resolve: keep the FIRST occurrence of each SI and fill the duplicates
 * and blanks with the missing values in hole order. Always yields a valid 1–18
 * permutation, and never moves an SI the organiser has already got right.
 */
export const autoFixSI = (sis = []) => {
  const padded = Array.from({ length: 18 }, (_, i) => sis[i]);
  const seen = new Set();
  const needs = [];
  const result = padded.map((v, idx) => {
    const n = asSI(v);
    if (n && !seen.has(n)) { seen.add(n); return n; }
    needs.push(idx);
    return null;
  });
  const fill = [];
  for (let n = 1; n <= 18; n++) if (!seen.has(n)) fill.push(n);
  needs.forEach((idx, i) => { result[idx] = fill[i] ?? ''; });
  return result;
};
